export type SiteItemType="field-trailer"|"office-trailer"|"dumpster"|"porta-potty"|"generator"|"crane-tower"|"crane-mobile"|"staging-area"|"soil-stockpile"|"gas-meter"|"water-service"|"electric-meter"|"temp-power"|"fire-hydrant"|"concrete-washout"|"fuel-tank"|"dewatering"|"sewer-connect"|"parking"|"first-aid"|"material-lift"|"temp-office";
export type SiteLineType="property"|"lod"|"temp-fence"|"haul-route"|"limit-work"|"utility-gas"|"utility-water"|"utility-electric"|"erosion-control"|"custom";
export interface SiteIcon{id:string;type:SiteItemType;x:number;y:number;label:string;
  /** Ground the item takes, in feet. Absent means "whatever this type
   *  usually is" — see DEFAULT_FOOTPRINT_FT — so plans drawn before
   *  items had sizes open with trailers already trailer-sized. */
  sizeFt?:{w:number;h:number};
  /** Degrees clockwise from north. A 45' trailer that can only sit square
   *  to the sheet either lies about where it is or about the room it
   *  leaves, and the drawing exists to answer that. */
  rotation?:number;
  /** Drawn in a colour of its own rather than the one its type ships with.
   *  The legend follows the drawing, so a recoloured item is still decoded
   *  correctly on the printed sheet. */
  color?:string;
  /** How much of the drawing underneath shows through, 0 to 1.
   *  A footprint is translucent by default so the survey beneath it can
   *  still be read; on a busy sheet that can be too faint to find, and the
   *  person looking at it is the one who knows which it is. */
  fillOpacity?:number;
  /** The weight of its outline. The outline is what gives a translucent
   *  footprint an edge; zero takes it away again. */
  strokeWidth?:number;
  /**
   * A distance around it that matters, in feet, drawn as a ring.
   *
   * What a crane can reach and must not swing outside of, how far a pump
   * truck can place from where it parks, the spacing a geothermal bore needs
   * off its neighbour, the longest a temporary service can run from the
   * meter. All the same drawing: a circle at a stated radius, which answers
   * the question the sheet exists to answer instead of leaving it to be
   * discovered on the day.
   */
  offsetFt?:number;
  /** Kept, but not drawn. A radius is usually wanted while the thing is being
   *  placed and in the way once it is. */
  offsetHidden?:boolean;
  /** How far the label has been pulled off the thing it labels, in
   *  percent. Stored as an offset rather than a position so a label
   *  nudged clear of a clash stays clear when the element is moved. */
  labelOffset?:{x:number;y:number};
  /** Held still: selectable and copyable, but it will not move, reshape,
   *  turn or delete until it is unlocked. What is surveyed on a site plan
   *  and what gets rearranged every week sit on the same sheet, and a drag
   *  aimed at one should not be able to shift the other. */
  locked?:boolean;
  note?:string;}
export interface SiteLine{id:string;lineType:SiteLineType;points:{x:number;y:number}[];label:string;strokeColor?:string;strokeWidth?:number;strokeDash?:string;
  /** A clearance either side of the run, in feet, drawn as a band: the width
   *  a haul route really takes, the easement a utility needs, how far a fence
   *  has to stand off the thing it protects. */
  offsetFt?:number;
  /** Kept, but not drawn. */
  offsetHidden?:boolean;
  /**
   * Which side of the run the clearance is on.
   *
   * Both is a corridor -- a temporary road, the width an underground service
   * has to be kept clear of either way. One side is a stand-off: a fence off
   * the thing it protects, a setback off a boundary, and it is the commoner
   * of the two. Left and right are of the direction the line was drawn, which
   * nobody can be expected to remember, so the control says "one side" and
   * "the other" and the drawing answers which is which.
   */
  offsetSide?:"both"|"left"|"right";
  /** A design vehicle to check this route against: draws the width it
   *  really takes and flags the corners it cannot make. */
  vehicle?:string;
  /** Draw a smooth run through the vertices instead of straight spans.
   *  The vertices are unchanged either way, so this is a view switch and
   *  not a conversion anyone has to commit to. */
  curved?:boolean;
  /** How far the shape has been turned from how it was drawn.
   *  The vertices stay the truth -- a turn is applied to them -- so this
   *  is bookkeeping, there to show a bearing and take one typed in. */
  rotation?:number;
  /** How far the label has been pulled off the thing it labels, in
   *  percent. Stored as an offset rather than a position so a label
   *  nudged clear of a clash stays clear when the element is moved. */
  labelOffset?:{x:number;y:number};
  /** Held still: selectable and copyable, but it will not move, reshape,
   *  turn or delete until it is unlocked. What is surveyed on a site plan
   *  and what gets rearranged every week sit on the same sheet, and a drag
   *  aimed at one should not be able to shift the other. */
  locked?:boolean;
  note?:string;}
export interface SiteArea{id:string;areaType:string;points:{x:number;y:number}[];label:string;color:string;fillColor?:string;fillOpacity?:number;strokeWidth?:number;
  curved?:boolean;
  /** How far the shape has been turned from how it was drawn.
   *  The vertices stay the truth -- a turn is applied to them -- so this
   *  is bookkeeping, there to show a bearing and take one typed in. */
  rotation?:number;
  /** How far the label has been pulled off the thing it labels, in
   *  percent. Stored as an offset rather than a position so a label
   *  nudged clear of a clash stays clear when the element is moved. */
  labelOffset?:{x:number;y:number};
  /** Held still: selectable and copyable, but it will not move, reshape,
   *  turn or delete until it is unlocked. What is surveyed on a site plan
   *  and what gets rearranged every week sit on the same sheet, and a drag
   *  aimed at one should not be able to shift the other. */
  locked?:boolean;
  note?:string;}
export interface SiteCircle{id:string;cx:number;cy:number;
  /** Legacy: radius in screen pixels, which changed meaning when the
   *  window was resized. Read only when rPct is absent. */
  r:number;
  /** Radius in percent of the drawing width — stable across window sizes. */
  rPct?:number;
  label:string;color:string;strokeWidth?:number;strokeDash?:string;
  /** How far the label has been pulled off the thing it labels, in
   *  percent. Stored as an offset rather than a position so a label
   *  nudged clear of a clash stays clear when the element is moved. */
  labelOffset?:{x:number;y:number};
  /** Held still: selectable and copyable, but it will not move, reshape,
   *  turn or delete until it is unlocked. What is surveyed on a site plan
   *  and what gets rearranged every week sit on the same sheet, and a drag
   *  aimed at one should not be able to shift the other. */
  locked?:boolean;
  note?:string;}

/**
 * A measured distance between two points.
 *
 * Not decoration: a drawing arrives with dimensions already on it, and the
 * only way to know whether this app agrees with the surveyor is to measure
 * one and compare. A dimension always reads what it measures -- there is no
 * way to type a value onto one, because a dimension that says what someone
 * wishes were true looks exactly like evidence.
 */
export interface SiteDimension{id:string;
  a:{x:number;y:number};
  b:{x:number;y:number};
  /** How far the dimension line is pushed off what it measures, in percent
   *  of the drawing width. Zero puts it straight through the measurement. */
  offset?:number;
  color?:string;
  locked?:boolean;
  note?:string;}

/** Free text anywhere on the drawing.
 *
 * Not every note belongs to something. A sequencing caveat, a phone number,
 * a reminder that the neighbour's driveway stays open — these are what people
 * write on a marked-up plan, and with nowhere to put them they end up in an
 * email nobody opens next to the drawing. */
export interface SiteText{id:string;x:number;y:number;text:string;
  color?:string;size?:number;
  /** Draw a filled box behind the text, so it stays readable over a drawing. */
  boxed?:boolean;
  /** Degrees clockwise. Labels often run along a road or a property line. */
  rotation?:number;
  align?:"left"|"center"|"right";
  /** Held still -- see SiteIcon.locked. */
  locked?:boolean;}

/** One logistics plan: a free canvas. A job has as many as it needs — demo,
 *  excavation, crane set, topping out — and they are made by duplicating the
 *  one before and changing what moved, which is how a superintendent
 *  describes a site anyway. */
/** The three things a sheet says about itself, each placed by hand. */
export type BlockKind="legend"|"title"|"scale";
export interface SheetBlock{
  x?:number;y?:number;
  hidden?:boolean;
  /** How wide it is drawn, in the drawing's own units. The legend turns
   *  this into columns, so a wide legend is a short one. */
  w?:number;
  /** A size multiplier for the whole block. These are drawing annotations:
   *  they scale with the sheet, so what looks right on screen can be
   *  enormous on an ARCH D print, and the right size is the drafter's call
   *  rather than ours. */
  scale?:number;
}

export interface SitePlan{id:string;name:string;
  /** Where the legend, the title block and the graphic scale sit, in percent
   *  of the drawing. They live ON the sheet rather than on a strip under it:
   *  a CAD sheet is a standard size, and a drawing plus a band of extra
   *  height is none of them, so printing it to the size it was drawn for
   *  would have to shrink it and the scale would be gone. */
  blocks?:Partial<Record<BlockKind,SheetBlock>>;

  /** This sheet's number in the set. Blank means "SL-n by position", so a
   *  set is numbered without anyone typing, and an office with its own
   *  numbering is never overruled. */
  /** The day this sheet was issued, ISO. */
  issueDate?:string;
  /** What changed and when. The part people skip and the part that settles
   *  arguments: without it the second issue of a plan is indistinguishable
   *  from the first. */

  /** When this stage of the site is in effect, as ISO days. Optional: a plan
   *  nobody has dated is a plan nobody has dated, not an error. Dates are
   *  what turn a pile of alternates into a sequence an owner can read. */
  phase?:{start?:string;end?:string};
imageDataUrl:string|null;
  /**
   * Pixels per inch of the drawing as it was brought in.
   *
   * A PDF page is measured in points, so rendering one at 2x gives 144 pixels
   * to the inch, not the 96 a screen image is assumed to have. Without this
   * the app read a 36" sheet as 54" wide -- and then every named scale, every
   * annotation sized in points of it, and the graphic scale bar itself were
   * half as big again as they should have been.
   */
  imageDpi?:number;
  /**
   * How big the app's own marks are drawn, as a multiple of true paper size.
   *
   * Annotation is sized in points of the sheet so that it prints true. On a
   * D sheet of a four-acre site that is honest and unreadable: ten-point type
   * is a fortieth of an inch of screen, and a four-foot item is a dot. A
   * markup is not drawing annotation -- it is written over the drawing to be
   * seen at a glance -- so how large it should be is a judgement about this
   * sheet, and this is where it is made.
   */
  markupScale?:number;
  icons:SiteIcon[];lines:SiteLine[];areas:SiteArea[];circles:SiteCircle[];texts?:SiteText[];dimensions?:SiteDimension[];notes:string;scale:{preset:string;
    /** Feet per 1% of the drawing width — stable across window sizes. */
    feetPerPct?:number|null;
    /** Legacy: feet per screen pixel, converted forward on load. */
    feetPerPx?:number|null};}

/** The module. The top-level drawing fields ARE the first plan, left where
 *  they have always been so every plan saved before this loads and edits
 *  unchanged; `plans` carries any after it. */
export interface SiteLogistics{
  /** Who prepared these drawings, and for whom. One answer per job, not per
   *  sheet. */
  titleBlock?:import("./titleBlock").TitleBlock;
imageDataUrl:string|null;
  /** Pixels per inch of the drawing as it was brought in — see SiteLogistics. */
  imageDpi?:number;
  /** How big the app's own marks are drawn, as a multiple — see SiteLogistics. */
  markupScale?:number;
  icons:SiteIcon[];lines:SiteLine[];areas:SiteArea[];circles:SiteCircle[];texts?:SiteText[];dimensions?:SiteDimension[];blocks?:Partial<Record<BlockKind,SheetBlock>>;notes:string;scale:{preset:string;
    /** Feet per 1% of the drawing width — stable across window sizes. */
    feetPerPct?:number|null;
    /** Legacy: feet per screen pixel, converted forward on load. */
    feetPerPx?:number|null};
  plans?:SitePlan[];
  activePlanId?:string;
  /** Name of the first plan, which lives in the top-level fields. */
  firstPlanName?:string;
  /** Whether element names are drawn on the plan. The legend names
   *  everything regardless, so this is about how busy the drawing looks,
   *  not about whether the information is available. */
  showLabels?:boolean;}
export const SITE_ICON_CONFIG:Record<SiteItemType,{label:string;abbr:string;color:string;category:string}>={
  "field-trailer":   {label:"Field Trailer",   abbr:"TRL",color:"#4fd1c5",category:"Facilities"},
  "office-trailer":  {label:"Office Trailer",  abbr:"OFF",color:"#4fd1c5",category:"Facilities"},
  "dumpster":        {label:"Dumpster",        abbr:"DMP",color:"#f6ad55",category:"Facilities"},
  "porta-potty":     {label:"Porta-Potty",     abbr:"PP", color:"#68d391",category:"Facilities"},
  "first-aid":       {label:"First Aid",       abbr:"FA", color:"#fc8181",category:"Facilities"},
  "parking":         {label:"Parking",         abbr:"PKG",color:"#63b3ed",category:"Facilities"},
  "temp-office":     {label:"Temp Office",     abbr:"OFC",color:"#b794f4",category:"Facilities"},
  "generator":       {label:"Generator",       abbr:"GEN",color:"#ecc94b",category:"Equipment"},
  "crane-tower":     {label:"Tower Crane",     abbr:"TCR",color:"#fc8181",category:"Equipment"},
  "crane-mobile":    {label:"Mobile Crane",    abbr:"MCR",color:"#fc8181",category:"Equipment"},
  "material-lift":   {label:"Material Lift",   abbr:"MLT",color:"#f6ad55",category:"Equipment"},
  "dewatering":      {label:"Dewatering",      abbr:"DWP",color:"#63b3ed",category:"Equipment"},
  "fuel-tank":       {label:"Fuel Tank",       abbr:"FUL",color:"#f6ad55",category:"Equipment"},
  "staging-area":    {label:"Staging Area",    abbr:"STG",color:"#76e4f7",category:"Site"},
  "soil-stockpile":  {label:"Soil Stockpile",  abbr:"STO",color:"#d69e2e",category:"Site"},
  "concrete-washout":{label:"Concrete Washout",abbr:"CW", color:"#a0aec0",category:"Site"},
  "gas-meter":       {label:"Gas Meter",       abbr:"GM", color:"#f6ad55",category:"Utilities"},
  "water-service":   {label:"Water Service",   abbr:"WS", color:"#4299e1",category:"Utilities"},
  "electric-meter":  {label:"Electric Meter",  abbr:"EM", color:"#ecc94b",category:"Utilities"},
  "temp-power":      {label:"Temp Power",      abbr:"TP", color:"#ecc94b",category:"Utilities"},
  "fire-hydrant":    {label:"Fire Hydrant",    abbr:"FH", color:"#fc8181",category:"Utilities"},
  "sewer-connect":   {label:"Sewer Connect",   abbr:"SC", color:"#9f7aea",category:"Utilities"},
};
export const SITE_LINE_CONFIG:Record<SiteLineType,{label:string;color:string;dash:string;width:number}>={
  // The boundary everything else on the sheet is measured off. Dash-dot in
  // red, which is how a property line has been drawn for a hundred years.
  "property":       {label:"Property Line",        color:"#e53e3e",dash:"14,4,3,4",width:2},
  "lod":            {label:"Limit of Disturbance", color:"#fc8181",dash:"10,5",  width:3},
  "temp-fence":     {label:"Temp Fence",           color:"#f6ad55",dash:"6,4",   width:2},
  "haul-route":     {label:"Haul Route",           color:"#ecc94b",dash:"14,4",  width:3},
  "limit-work":     {label:"Limit of Work",        color:"#b794f4",dash:"8,3,2,3",width:2},
  "utility-gas":    {label:"Gas Line",             color:"#f6ad55",dash:"none",  width:2},
  "utility-water":  {label:"Water Line",           color:"#4299e1",dash:"none",  width:2},
  "utility-electric":{label:"Electric Line",       color:"#ecc94b",dash:"none",  width:2},
  "erosion-control":{label:"Erosion Control",      color:"#68d391",dash:"4,4",   width:2},
  // Whatever this job has that the list does not: fencing, a sleeve route, a
  // crane mat run. Named by whatever the drawer calls it rather than by a
  // type, which is why the key groups custom lines by their label and not by
  // this one -- see legendRows. Keeping it out of the defaults is the point:
  // a palette that lists everything anyone has ever needed is a catalogue to
  // read through, not a tool.
  "custom":         {label:"Custom Line",          color:"#a0aec0",dash:"none",  width:2},
};
export const SITE_AREA_CONFIG:{[k:string]:{label:string;color:string;fillOpacity:number}}={
  "parking-lot":         {label:"Parking Lot",         color:"#63b3ed",fillOpacity:0.28},
  "temp-road":           {label:"Temporary Road",       color:"#a0aec0",fillOpacity:0.32},
  "stormwater-basin":    {label:"Stormwater Basin",     color:"#4299e1",fillOpacity:0.32},
  "soil-stockpile-area": {label:"Soil Stockpile",       color:"#d69e2e",fillOpacity:0.38},
  "staging-pad":         {label:"Staging Pad",          color:"#76e4f7",fillOpacity:0.25},
  "laydown-area":        {label:"Laydown Area",         color:"#ecc94b",fillOpacity:0.28},
  "dewatering-basin":    {label:"Dewatering Basin",     color:"#9f7aea",fillOpacity:0.28},
  "custom-area":         {label:"Custom Area",          color:"#68d391",fillOpacity:0.28},
};
export const SCALE_PRESETS:{id:string;label:string;feetPerPx:number|null}[]=[
  {id:"none",  label:"— No Scale —",     feetPerPx:null},
  {id:"i5",    label:'1" = 5\'',         feetPerPx:5/96},
  {id:"i10",   label:'1" = 10\'',        feetPerPx:10/96},
  {id:"i20",   label:'1" = 20\'',        feetPerPx:20/96},
  {id:"i30",   label:'1" = 30\'',        feetPerPx:30/96},
  {id:"i40",   label:'1" = 40\'',        feetPerPx:40/96},
  {id:"i50",   label:'1" = 50\'',        feetPerPx:50/96},
  {id:"i100",  label:'1" = 100\'',       feetPerPx:100/96},
  {id:"i200",  label:'1" = 200\'',       feetPerPx:200/96},
  {id:"r50",   label:"1:50",             feetPerPx:50/(96*12)},
  {id:"r100",  label:"1:100",            feetPerPx:100/(96*12)},
  {id:"r200",  label:"1:200",            feetPerPx:200/(96*12)},
  {id:"r500",  label:"1:500",            feetPerPx:500/(96*12)},
  {id:"r1000", label:"1:1000",           feetPerPx:1000/(96*12)},
  {id:"custom",label:"Custom (calibrate)",feetPerPx:null},
];
